import React, {
  useState,
  useMemo,
  useCallback,
  useRef,
  useEffect,
  startTransition,
  forwardRef,
  useImperativeHandle,
} from 'react';
import { Box, Alert, Snackbar, Chip } from '@mui/material';
import type {
  SortingState,
  ColumnFiltersState,
  VisibilityState,
  ColumnSizingState,
  RowSelectionState,
} from '@tanstack/react-table';
import type { Command, GridMetadata } from '../../menu/types';
import type { ColumnMetadata, LookupDataItem } from '../types';
import type { NavigationRequestOptions } from '../types/navigation';
import { DataGrid } from './data-display/DataGrid';
import { GridActionToolbar } from './toolbar/GridActionToolbar';
import ProjectBoardPickerDialog from './dialogs/ProjectBoardPickerDialog';
import CompanyBankAccountDialog from './dialogs/CompanyBankAccountDialog';
import EmployeeManagerDialog from './dialogs/EmployeeManagerDialog';
import SubscriptionProofBankImportDialog from './dialogs/SubscriptionProofBankImportDialog';
import CreateTaskForTeamDialog from './dialogs/CreateTaskForTeamDialog';
import CreateTasksAndWorkLogsDialog from './dialogs/CreateTasksAndWorkLogsDialog';
import { MissingEmployeeRatesDialog } from './dialogs/MissingEmployeeRatesDialog';
import { BankStatementManagementImportDialog } from '../../bank-statement-import/components/BankStatementManagementImportDialog';
import type { ProjectBoardModal } from '../commands/types';
import { useCommandHandler, useDeleteScreenshot, useFinanceEmailRowActions } from '../hooks';
import {
  useCopyRowsMutation,
  useDeleteBatchMutation,
  useUpdateBatchMutation,
  useGenerateAndSendPayrollMutation,
} from '../api/gridApi';
import { DeleteConfirmDialog } from '../../../components/common/DeleteConfirmDialog';
import { ConfirmDialog } from '../../../components/common/ConfirmDialog';
import { useConfirmDialog } from '../hooks/useConfirmDialog';
import { sxColFillHidden, sxSuccessAlert } from '../../../theme/sxMixins';
import type { UserManagementModal } from '../commands/types';
import { UserManagementDialogs } from './UserManagementDialogs';
import DOMPurify from 'dompurify';
import { useGridReactCommand } from '../react-commands';
import { getGridDateTimeMode, isTabGrid, type GridDateTimeMode } from '../utils';
import { JsMethodSupportNotifier } from '../../js-methods';
import type { ConfigurationTables } from '../hooks';
import { useGridGrouping } from '../hooks/useGridGrouping';
import { isDetailNavigationGrid } from '../commands/utils';
import { FILTER_PANEL_COMMAND_ID, useFilterPanelVisibility } from '../commands/filterPanelState';
import { resetChangeTotalState } from '../commands/handlers/changeTotal';

const EMPTY_MODIFIED_ROWS = new Map<
  string,
  { rowId: string; data: Record<string, unknown>; isNew: boolean }
>();

/**
 * Imperative handle exposed via forwardRef so a parent (GridPage) can trigger
 * command execution for commands that live in the page header (save, revert,
 * import are handled at grid level but called from outside the component).
 */
export type GridWithCommandsRef = {
  handleCommandClick: (command: Command) => Promise<void>;
};

/**
 * Props for GridWithCommands component
 * Combines data grid functionality with command toolbar
 */
export type GridWithCommandsProps = {
  focusRowId?: string;
  // Grid metadata and configuration
  metadata: GridMetadata;
  pageTitle?: string;
  columns: ColumnMetadata[];
  commands?: Command[];

  // Data
  data: Record<string, unknown>[];
  gridData: Record<string, unknown>[];
  lookupMaps?: Map<string, Record<string, string>>;
  configurationTables?: ConfigurationTables;
  filterLookupMaps?: Map<string, Record<string, string>>;
  rawLookupItems?: Map<string, LookupDataItem[]>;
  dropdownBaseFilter?: unknown;
  totalCount?: number;
  serverSummary?: string[] | null;

  // Pagination
  page?: number;
  pageSize?: number;
  onPageChange?: (page: number) => void;
  onPageSizeChange?: (pageSize: number) => void;

  // Sorting and filtering (using TanStack Table types)
  onSortChange?: (sorting: SortingState) => void;
  onFilterChange?: (filters: ColumnFiltersState) => void;
  onDateRangeChange?: (startDate: string, endDate: string) => void;
  filterResetTrigger?: number;

  // Row operations
  onEdit?: (row: Record<string, unknown>) => void;
  onDelete?: (row: Record<string, unknown>) => void;
  onRestore?: (row: Record<string, unknown>) => void | Promise<void>;
  onDataUpdate?: (rowIndex: number, columnId: string, value: unknown) => void;
  onRowDataUpdate?: (rowIndex: number, patch: Record<string, unknown>) => void;

  // State tracking
  modifiedCells?: Set<string>;
  markedForDeletion?: Set<string>;
  hasUnsavedChanges?: boolean;
  isBatchSaving?: boolean;
  modifiedRows?: Map<string, { rowId: string; data: Record<string, unknown>; isNew: boolean }>;
  onRollbackRow?: (rowId: string) => void;

  // Callbacks for commands
  onRefresh?: () => void;
  onSave?: () => Promise<boolean | void>;
  onCancel?: () => void;
  onAddRow?: () => void;
  onAddRowWithData?: (fieldData: Record<string, unknown> | Record<string, unknown>[]) => void;
  onResetFilters?: () => void;
  onToggleGridVisibilityFilter?: (command: Command) => void;
  commandCaptionOverrides?: Record<string, string>;
  onSelectionChange?: (selectedRows: Set<string> | undefined) => void;

  // Navigation
  navigate?: (path: string | number, options?: NavigationRequestOptions) => void;
  currentPath?: string;

  // Grid configuration
  loading?: boolean;
  overlayLoading?: boolean;
  serverSide?: boolean;
  inlineEditable?: boolean;
  nodeKey?: string;

  // Parent context (for child grids)
  parentId?: string;
  entityId?: string;

  // Toolbar configuration
  showMetadataChips?: boolean;

  // Grid dimensions
  maxHeight?: string;
  shrinkToRowNumbers?: boolean;

  // Search
  searchTerm?: string;
  onSearchChange?: (term: string) => void;

  // Validation
  validationErrors?: Map<string, string>;

  // Show deleted
  showDeleted?: boolean;
  onShowDeletedToggle?: () => void;

  // Grid state persistence
  initialSorting?: SortingState;
  initialColumnFilters?: ColumnFiltersState;
  columnFiltersSessionKey?: string;
  savedColumnVisibility?: VisibilityState;
  savedColumnSizing?: ColumnSizingState;
  savedColumnOrder?: string[];
  onSaveGridState?: (
    columnVisibility: VisibilityState,
    columnSizing: ColumnSizingState,
    sorting: SortingState,
    columnOrder: string[]
  ) => void;
  onClearGridState?: () => void;

  /**
   * When true, Save / Revert / Import are rendered by the parent (GridPage
   * header) rather than the toolbar.  DetailFormGridSection should leave this
   * false so those commands stay in the embedded toolbar.
   */
  headerActionsExtracted?: boolean;

  // range for timesheet
  dateRange?: { startDate?: string; endDate?: string };
};

/**
 * GridWithCommands - Unified grid component with command toolbar
 *
 * This component combines:
 * - GridActionToolbar: Command buttons with state management
 * - Error alerts: Display command execution errors
 * - DataGrid: Main data display with all features
 *
 * Benefits:
 * - Encapsulates command handling logic
 * - Reduces duplication between GridPage and DetailFormGridSection
 * - Provides consistent command execution and error handling
 */
export const GridWithCommands = forwardRef<GridWithCommandsRef, GridWithCommandsProps>(
  (
    {
      metadata,
      focusRowId,
      pageTitle,
      columns,
      commands,
      data,
      gridData,
      lookupMaps,
      configurationTables,
      filterLookupMaps,
      rawLookupItems,
      dropdownBaseFilter,
      totalCount = 0,
      serverSummary,
      page,
      pageSize,
      onPageChange,
      onPageSizeChange,
      onSortChange,
      onFilterChange,
      onDateRangeChange,
      filterResetTrigger,
      onEdit,
      onDelete,
      onRestore,
      onDataUpdate,
      onRowDataUpdate,
      modifiedCells,
      markedForDeletion,
      hasUnsavedChanges = false,
      isBatchSaving = false,
      modifiedRows = EMPTY_MODIFIED_ROWS,
      onRollbackRow,
      onRefresh,
      onSave,
      onCancel,
      onAddRow,
      onAddRowWithData,
      onResetFilters,
      onToggleGridVisibilityFilter,
      commandCaptionOverrides,
      onSelectionChange,
      navigate,
      currentPath,
      loading = false,
      overlayLoading = false,
      inlineEditable = false,
      nodeKey,
      parentId,
      entityId,
      showMetadataChips = true,
      maxHeight,
      shrinkToRowNumbers,
      searchTerm,
      onSearchChange,
      validationErrors,
      showDeleted,
      onShowDeletedToggle,
      initialSorting,
      initialColumnFilters,
      columnFiltersSessionKey,
      savedColumnVisibility,
      savedColumnSizing,
      savedColumnOrder,
      onSaveGridState,
      onClearGridState,
      headerActionsExtracted = false,
      dateRange,
    },
    ref
  ) => {
    useEffect(() => {
      resetChangeTotalState(metadata.contentKey);
      return () => resetChangeTotalState(metadata.contentKey);
    }, [metadata.contentKey]);

    const showFilterPanel = metadata.showFilterPanel !== false;
    const hasFilterPanelToggle =
      commands?.some((command) => command.alias === FILTER_PANEL_COMMAND_ID) ?? false;
    const [filterPanelVisible, toggleFilterPanel] = useFilterPanelVisibility(
      metadata.contentKey ?? metadata.contentId,
      hasFilterPanelToggle
    );
    const showSearchPanel = metadata.showSearchPanel !== false;
    const shouldShowToolbar = (commands?.length ?? 0) > 0 || showSearchPanel || showFilterPanel;
    const dateFilterCommand = commands?.find((command) => command.alias === 'zgridfilter');
    const dateTimeMode: GridDateTimeMode | undefined = dateFilterCommand
      ? getGridDateTimeMode(dateFilterCommand.settings)
      : undefined;

    // Snackbar state for success/error feedback
    const [snackbar, setSnackbar] = useState<{
      open: boolean;
      message: string;
      severity: 'success' | 'error';
    }>({ open: false, message: '', severity: 'success' });
    const { onRowActivate, activeRenderer } = useGridReactCommand(commands, metadata);

    // Keep export grouping state in sync with the grouping controls rendered by DataGrid.
    const { groupFields: initialGroupFields, groupCaptions } = useGridGrouping([], columns);
    const [groupFields, setGroupFields] = useState(initialGroupFields);
    const [exportState, setExportState] = useState<{
      rows: Record<string, unknown>[];
      columns: ColumnMetadata[];
      hasActiveFilters: boolean;
    }>({ rows: data, columns, hasActiveFilters: false });
    const handleExportStateChange = useCallback(
      (rows: Record<string, unknown>[], visibleColumnIds: string[], hasActiveFilters: boolean) => {
        const visibleColumns = visibleColumnIds
          .map((id) => columns.find((column) => (column.field || column.dataField) === id))
          .filter((column): column is ColumnMetadata => Boolean(column));
        setExportState({
          rows,
          columns: visibleColumns.length > 0 ? visibleColumns : columns,
          hasActiveFilters,
        });
      },
      [columns]
    );

    // Project board modal state (for multi-selection open board command)
    const [projectBoardModal, setProjectBoardModal] = useState<ProjectBoardModal | null>(null);
    const [userManagementModal, setUserManagementModal] = useState<UserManagementModal | null>(
      null
    );
    const [companyBankAccountModal, setCompanyBankAccountModal] = useState<{
      command: Command;
      invoiceIds: number[];
      targetFieldName: string;
    } | null>(null);
    const [employeeManagerModal, setEmployeeManagerModal] = useState<{
      command: Command;
      employeeIds: number[];
      managerFieldName: string;
      managerFieldKey: string;
      currentManagerIds: number[];
      defaultFilters?: ColumnMetadata['defaultFilters'];
    } | null>(null);
    const [subscriptionProofBankImportCommand, setSubscriptionProofBankImportCommand] =
      useState<Command | null>(null);
    const [createTaskForTeamModal, setCreateTaskForTeamModal] = useState<{
      command: Command;
      projectId: number;
    } | null>(null);
    const [createTasksAndWorkLogsModal, setCreateTasksAndWorkLogsModal] = useState<{
      command: Command;
      projectId: number;
    } | null>(null);
    const [missingEmployeeRatesCommand, setMissingEmployeeRatesCommand] = useState<Command | null>(
      null
    );
    const [bankStatementManagementImportCommand, setBankStatementManagementImportCommand] =
      useState<Command | null>(null);

    // ── Confirmation dialogs ───────────────────────────────────────────────
    // Generic (non-destructive): e.g. "Send payroll documents?"
    const confirmDialog = useConfirmDialog();
    // Destructive (delete): e.g. group-delete command handlers
    const deleteConfirmDialog = useConfirmDialog();
    // Column visibility state for column chooser (persisted via DB)
    const [columnVisibility, setColumnVisibility] = useState<VisibilityState>(() =>
      savedColumnVisibility && Object.keys(savedColumnVisibility).length > 0
        ? savedColumnVisibility
        : {}
    );

    // Column sizing state from persisted DB state
    const [columnSizing, setColumnSizing] = useState<ColumnSizingState>(
      () => savedColumnSizing ?? {}
    );

    const [viewOverride, setViewOverride] = useState<{
      rows: Record<string, unknown>[];
      title?: string;
    } | null>(null);

    const [columnOrder, setColumnOrder] = useState<string[]>(() => savedColumnOrder ?? []);

    // Sync DB state when props change (adjust-state-during-render pattern with state tracking)
    const visibilityDirtyRef = useRef(false);
    const latestVisibilityRef = useRef(columnVisibility);
    const latestSizingRef = useRef(columnSizing);
    const latestSortingRef = useRef<SortingState>([]);
    const [prevSavedVisibility, setPrevSavedVisibility] = useState(savedColumnVisibility);
    const [prevSavedSizing, setPrevSavedSizing] = useState(savedColumnSizing);
    const latestOrderRef = useRef<string[]>(columnOrder);
    const [prevSavedOrder, setPrevSavedOrder] = useState(savedColumnOrder);

    const normalizedOrder =
      savedColumnOrder && savedColumnOrder.length > 0 ? savedColumnOrder : undefined;

    if (normalizedOrder !== prevSavedOrder) {
      setPrevSavedOrder(normalizedOrder);
      if (normalizedOrder) {
        setColumnOrder(normalizedOrder);
      }
    }

    React.useEffect(() => {
      setViewOverride(null);
    }, [dateRange?.startDate, dateRange?.endDate]);

    const handleColumnOrderChange = useCallback(
      (order: string[]) => {
        latestOrderRef.current = order;
        setColumnOrder(order);
        onSaveGridState?.(columnVisibility, columnSizing, latestSortingRef.current, order);
      },
      [columnVisibility, columnSizing, onSaveGridState]
    );

    // Normalize empty objects to undefined so that {} and undefined are treated the same
    const normalizedVisibility =
      savedColumnVisibility && Object.keys(savedColumnVisibility).length > 0
        ? savedColumnVisibility
        : undefined;
    const normalizedSizing =
      savedColumnSizing && Object.keys(savedColumnSizing).length > 0
        ? savedColumnSizing
        : undefined;

    if (normalizedVisibility !== prevSavedVisibility) {
      setPrevSavedVisibility(normalizedVisibility);
      if (normalizedVisibility) {
        setColumnVisibility(normalizedVisibility);
      }
    }

    if (normalizedSizing !== prevSavedSizing) {
      setPrevSavedSizing(normalizedSizing);
      if (normalizedSizing) {
        setColumnSizing(normalizedSizing);
      }
    }

    const handleReplaceGridView = useCallback((rows: Record<string, unknown>[], title?: string) => {
      setViewOverride({ rows, title });
    }, []);

    const handleColumnSizingChange = useCallback((sizing: ColumnSizingState) => {
      latestSizingRef.current = sizing;
      setColumnSizing(sizing);
    }, []);

    const handleSortingChange = useCallback((s: SortingState) => {
      latestSortingRef.current = s;
    }, []);

    // Grid state save/clear wrappers — forward current column visibility + sizing + sorting
    const handleSaveGridState = useCallback(() => {
      onSaveGridState?.(columnVisibility, columnSizing, latestSortingRef.current, columnOrder);
      setSnackbar({ open: true, message: 'Grid state saved successfully', severity: 'success' });
    }, [onSaveGridState, columnVisibility, columnSizing, columnOrder]);

    const handleClearGridState = useCallback(() => {
      onClearGridState?.();
      setColumnVisibility({});
      setColumnSizing({});
      setColumnOrder([]);
      setSnackbar({ open: true, message: 'Grid state cleared successfully', severity: 'success' });
    }, [onClearGridState]);

    const handleRestore = useCallback(
      async (row: Record<string, unknown>) => {
        if (!onRestore || !row.Id) return;
        try {
          await onRestore(row);
          setSnackbar({
            open: true,
            message: 'Entity restored successfully',
            severity: 'success',
          });
        } catch (error) {
          setSnackbar({
            open: true,
            message: `Restore failed: ${error instanceof Error ? error.message : 'Unknown error'}`,
            severity: 'error',
          });
        }
      },
      [onRestore]
    );

    const handleColumnVisibilityChange = useCallback((visibility: VisibilityState) => {
      latestVisibilityRef.current = visibility;
      startTransition(() => {
        setColumnVisibility(visibility);
      });
      visibilityDirtyRef.current = true;
    }, []);

    // Auto-persist column visibility on column chooser close via full save
    const handleColumnChooserClose = useCallback(() => {
      if (!visibilityDirtyRef.current) return;
      visibilityDirtyRef.current = false;
      onSaveGridState?.(
        latestVisibilityRef.current,
        latestSizingRef.current,
        latestSortingRef.current,
        latestOrderRef.current
      );
    }, [onSaveGridState]);

    // Row selection: always enabled (single-select on row click)
    // Multi-select checkboxes only when there is at least one group-action command
    const isDetailNav = isDetailNavigationGrid(metadata, commands);
    const enableRowSelection = true;
    const enableMultiSelect = useMemo(
      () => commands?.some((cmd) => cmd.isGroup) ?? false,
      [commands]
    );

    const canEditSavedRows = useMemo(() => {
      const editCommand = commands?.find((cmd) => cmd.alias === 'edit');
      return editCommand?.isInlineEdit ?? false;
    }, [commands]);

    const [rowSelection, setRowSelection] = useState<RowSelectionState>({});
    const focusedRowId = useRef<string | null>(null);
    useEffect(() => {
      if (!focusRowId || focusedRowId.current === focusRowId) return;
      if (!data.some((row) => String(row.Id) === focusRowId)) return;
      const frame = requestAnimationFrame(() => {
        setRowSelection({ [focusRowId]: true });
        focusedRowId.current = focusRowId;
      });
      return () => cancelAnimationFrame(frame);
    }, [data, focusRowId]);
    const [canResetFilters, setCanResetFilters] = useState(false);

    // Copy rows mutation
    const [copyRowsTrigger] = useCopyRowsMutation();

    // Delete batch mutation
    const [deleteBatchTrigger] = useDeleteBatchMutation();

    const [generateAndSendPayrollTrigger] = useGenerateAndSendPayrollMutation();

    // Screenshot deletion — row-scoped action, wired directly to the backend
    // command "deleteScreenshot"
    const { onDeleteScreenshot, deletingScreenshotIds } = useDeleteScreenshot({
      commands,
      contentKey: metadata.contentKey,
      onRefresh,
      requestDeleteConfirmation: deleteConfirmDialog.request,
      onNotify: (message, severity) => setSnackbar({ open: true, message, severity }),
    });

    const {
      onArchiveFinanceEmail,
      onRestoreFinanceEmail,
      onMarkFinanceEmailSpam,
      onUnmarkFinanceEmailSpam,
    } = useFinanceEmailRowActions({
      commands,
      onRefresh,
      requestConfirmation: confirmDialog.request,
      onNotify: (message, severity) => setSnackbar({ open: true, message, severity }),
    });

    const clearSelection = useCallback(() => setRowSelection({}), []);
    const [updateBatchTrigger] = useUpdateBatchMutation();

    const openCompanyBankAccountPicker = useCallback(
      (command: Command, invoiceIds: number[], targetFieldName: string) => {
        setCompanyBankAccountModal({ command, invoiceIds, targetFieldName });
      },
      []
    );
    const openEmployeeManagerPicker = useCallback(
      (
        command: Command,
        employeeIds: number[],
        managerFieldName: string,
        managerFieldKey: string,
        currentManagerIds: number[],
        defaultFilters?: ColumnMetadata['defaultFilters']
      ) => {
        setEmployeeManagerModal({
          command,
          employeeIds,
          managerFieldName,
          managerFieldKey,
          currentManagerIds,
          defaultFilters,
        });
      },
      []
    );

    const openSubscriptionProofBankImportDialog = useCallback((command: Command) => {
      setSubscriptionProofBankImportCommand(command);
    }, []);

    const openBankStatementManagementImportDialog = useCallback((command: Command) => {
      setBankStatementManagementImportCommand(command);
    }, []);

    const openCreateTaskForTeamDialog = useCallback((command: Command, projectId: number) => {
      setCreateTaskForTeamModal({ command, projectId });
    }, []);

    const openCreateTasksAndWorkLogsDialog = useCallback((command: Command, projectId: number) => {
      setCreateTasksAndWorkLogsModal({ command, projectId });
    }, []);

    const selectedRows = useMemo(() => {
      const ids = Object.keys(rowSelection).filter((k) => rowSelection[k]);
      return ids.length > 0 ? new Set(ids) : undefined;
    }, [rowSelection]);

    React.useEffect(() => {
      onSelectionChange?.(selectedRows);
    }, [onSelectionChange, selectedRows]);

    const handleRollbackSelectedRows = useCallback(() => {
      if (!onRollbackRow) return;
      // Rollback all pending: modified rows + rows marked for deletion
      const pendingIds = new Set([...modifiedRows.keys(), ...(markedForDeletion ?? [])]);
      for (const rowId of pendingIds) {
        onRollbackRow(rowId);
      }
    }, [modifiedRows, markedForDeletion, onRollbackRow]);

    const generateAndSendPayroll = useCallback(
      (request: Parameters<typeof generateAndSendPayrollTrigger>[0]) =>
        generateAndSendPayrollTrigger(request).unwrap(),
      [generateAndSendPayrollTrigger]
    );

    // Command handler hook - provides unified command execution
    const { executeCommand, isExecuting } = useCommandHandler({
      metadata,
      pageTitle,
      columns,
      commands,
      gridData,
      displayData: data,
      exportData: exportState.rows,
      exportColumns: exportState.columns,
      hasActiveFilters: exportState.hasActiveFilters,
      serverSummary,
      hasUnsavedChanges,
      modifiedRows,
      modifiedCells: modifiedCells || new Set(),
      markedForDeletion: markedForDeletion || new Set(),
      onRefresh: onRefresh || (() => {}),
      onSave: onSave || (async () => false),
      onCancel: onCancel || (() => {}),
      onAddRow,
      onAddRowWithData,
      onDeleteRow: onDelete,
      onEditRow: onEdit,
      onResetFilters,
      toggleGridVisibilityFilter: onToggleGridVisibilityFilter,
      navigate: navigate || (() => {}),
      currentPath: currentPath || '',
      lookupMaps,
      configurationTables,
      parentId,
      entityId,
      selectedRows,
      copyRows: (args) => copyRowsTrigger(args).unwrap(),
      clearSelection,
      deleteBatchRows: (entities) => deleteBatchTrigger(entities).unwrap(),
      updateBatchRows: (request) => updateBatchTrigger(request).unwrap(),
      requestConfirmation: confirmDialog.request,
      requestDeleteConfirmation: deleteConfirmDialog.request,
      inlineEditable,
      openCompanyBankAccountPicker,
      openEmployeeManagerPicker,
      openSubscriptionProofBankImportDialog,
      openCreateTaskForTeamDialog,
      openCreateTasksAndWorkLogsDialog,
      openMissingEmployeeRatesDialog: setMissingEmployeeRatesCommand,
      openBankStatementManagementImportDialog,
      dateRange,
      onReplaceGridView: handleReplaceGridView,
      generateAndSendPayroll,
      groupFields,
      groupCaptions,
    });

    // Handle command click with error handling and refresh logic
    const handleCommandClick = useCallback(
      async (command: Command) => {
        const result = await executeCommand(command);

        // If the handler returned a modal payload, show the project picker dialog
        if (result.modal) {
          setProjectBoardModal(result.modal);
          return;
        }

        if (result.userManagementModal) {
          setUserManagementModal(result.userManagementModal);
          return;
        }

        // Handle command result
        if (result.success) {
          if (result.message) {
            setSnackbar({ open: true, message: result.message, severity: 'success' });
          }
          if (result.shouldRefresh && onRefresh) {
            setViewOverride(null);
            onRefresh();
          }
        } else if (result.error) {
          setSnackbar({ open: true, message: result.error, severity: 'error' });
        }
      },
      [executeCommand, onRefresh]
    );

    // ── Expose handleCommandClick to parent (for header-level buttons) ────────
    useImperativeHandle(ref, () => ({ handleCommandClick }), [handleCommandClick]);

    const handleSnackbarClose = useCallback(
      (_event?: React.SyntheticEvent | Event, reason?: string) => {
        if (reason === 'clickaway') return;
        setSnackbar((prev) => ({ ...prev, open: false }));
      },
      []
    );

    const effectiveOverlayLoading =
      overlayLoading ||
      (isExecuting && !confirmDialog.state.open && !deleteConfirmDialog.state.open);

    return (
      <Box
        sx={
          (shrinkToRowNumbers ?? metadata.shrinkToRowNumbers)
            ? { width: '100%', display: 'flex', flexDirection: 'column' }
            : { ...sxColFillHidden, width: '100%', height: '100%' }
        }
      >
        {shouldShowToolbar && (
          <GridActionToolbar
            metadata={metadata}
            commands={commands}
            hasUnsavedChanges={hasUnsavedChanges}
            isBatchSaving={isBatchSaving}
            onCommandClick={handleCommandClick}
            commandCaptionOverrides={commandCaptionOverrides}
            showMetadataChips={showMetadataChips}
            onDateRangeChange={onDateRangeChange}
            searchTerm={searchTerm}
            onSearchChange={showSearchPanel ? onSearchChange : undefined}
            showSearchPanel={showSearchPanel}
            columns={columns}
            showDeleted={showDeleted}
            onShowDeletedToggle={onShowDeletedToggle}
            columnVisibility={columnVisibility}
            onColumnVisibilityChange={handleColumnVisibilityChange}
            onSaveGridState={onSaveGridState ? handleSaveGridState : undefined}
            onClearGridState={onClearGridState ? handleClearGridState : undefined}
            hasSelectedRows={!!selectedRows && selectedRows.size > 0}
            markedForDeletion={markedForDeletion}
            hasPendingChanges={modifiedRows.size > 0 || (markedForDeletion?.size ?? 0) > 0}
            inlineEditable={inlineEditable}
            onRollbackSelectedRows={onRollbackRow ? handleRollbackSelectedRows : undefined}
            onColumnChooserClose={handleColumnChooserClose}
            canResetFilters={canResetFilters}
            filterPanelVisible={filterPanelVisible}
            filterPanelAvailable={showFilterPanel}
            onFilterPanelToggle={toggleFilterPanel}
            headerActionsExtracted={headerActionsExtracted}
            initialDateRange={dateRange}
          />
        )}

        <Box
          sx={
            (shrinkToRowNumbers ?? metadata.shrinkToRowNumbers)
              ? { mt: 1, mb: 1 }
              : { ...sxColFillHidden, mt: 1, mb: 1 }
          }
        >
          {viewOverride?.title && (
            <Chip
              label={viewOverride.title}
              size="small"
              color="info"
              onDelete={() => setViewOverride(null)}
              sx={{ mb: 1, alignSelf: 'flex-start' }}
            />
          )}
          <DataGrid
            focusRowId={focusRowId}
            data={viewOverride?.rows ?? data}
            columns={columns}
            lookupMaps={lookupMaps}
            filterLookupMaps={filterLookupMaps}
            rawLookupItems={rawLookupItems}
            dropdownBaseFilter={dropdownBaseFilter}
            totalCount={totalCount}
            serverSummary={serverSummary}
            page={page}
            pageSize={pageSize}
            usePaging={metadata?.usePaging}
            serverSide={metadata?.usePaging === true}
            onPageChange={metadata?.usePaging === true ? onPageChange : undefined}
            onPageSizeChange={onPageSizeChange}
            filterResetTrigger={filterResetTrigger}
            onRestore={onRestore ? handleRestore : undefined}
            isDetailNav={isDetailNav}
            onEdit={isDetailNav ? onEdit : undefined}
            onDelete={onDelete}
            loading={loading}
            overlayLoading={effectiveOverlayLoading}
            hidePaginationPanel={metadata.hidePaginationPanel === true}
            showPageSizeSelector={!isTabGrid(metadata.parentSource)}
            shrinkToRowNumbers={(shrinkToRowNumbers ?? metadata.shrinkToRowNumbers) === true}
            showLoadingPanel={metadata.showLoadingPanel !== false}
            wordWrap={metadata.wordWrap === true}
            nodeKey={nodeKey}
            parentId={parentId}
            onDataUpdate={onDataUpdate}
            onRowDataUpdate={onRowDataUpdate}
            modifiedCells={modifiedCells}
            markedForDeletion={markedForDeletion}
            inlineEditable={inlineEditable}
            canEditSavedRows={canEditSavedRows}
            maxHeight={maxHeight}
            searchTerm={showSearchPanel ? searchTerm : undefined}
            showSearchPanel={showSearchPanel}
            showFilterPanel={showFilterPanel && filterPanelVisible}
            validationErrors={validationErrors}
            columnVisibility={columnVisibility}
            onColumnVisibilityChange={handleColumnVisibilityChange}
            initialColumnOrder={columnOrder}
            onColumnOrderChange={handleColumnOrderChange}
            initialSorting={initialSorting}
            initialColumnFilters={initialColumnFilters}
            columnFiltersSessionKey={columnFiltersSessionKey}
            initialColumnSizing={columnSizing}
            onColumnSizingChange={handleColumnSizingChange}
            onSortChange={onSortChange}
            onSortingChange={handleSortingChange}
            onFilterChange={onFilterChange}
            onFilterSortActiveChange={setCanResetFilters}
            enableRowSelection={enableRowSelection}
            enableMultiSelect={enableMultiSelect}
            rowSelection={rowSelection}
            onRowSelectionChange={setRowSelection}
            onRowActivate={onRowActivate}
            onDeleteScreenshot={onDeleteScreenshot}
            deletingScreenshotIds={deletingScreenshotIds}
            onArchiveFinanceEmail={onArchiveFinanceEmail}
            onRestoreFinanceEmail={onRestoreFinanceEmail}
            onMarkFinanceEmailSpam={onMarkFinanceEmailSpam}
            onUnmarkFinanceEmailSpam={onUnmarkFinanceEmailSpam}
            dateTimeMode={dateTimeMode}
            userRelationDays={metadata.userRelationDays}
            userRelationVacationDays={metadata.userRelationVacationDays}
            canEditLockedWorkLogs={metadata.canEditLockedWorkLogs}
            currentUserRelationId={metadata.currentUserRelationId}
            configurationTables={configurationTables}
            dataOptionsMax={metadata.dataOptionsMax}
            onGroupFieldsChange={setGroupFields}
            onExportStateChange={handleExportStateChange}
          />
        </Box>

        {activeRenderer}

        <ConfirmDialog
          open={confirmDialog.state.open}
          message={confirmDialog.state.message}
          onConfirm={confirmDialog.confirm}
          onClose={confirmDialog.cancel}
        />

        <DeleteConfirmDialog
          open={deleteConfirmDialog.state.open}
          message={deleteConfirmDialog.state.message}
          onConfirm={deleteConfirmDialog.confirm}
          onClose={deleteConfirmDialog.cancel}
        />

        <ProjectBoardPickerDialog
          open={!!projectBoardModal}
          modal={projectBoardModal}
          onClose={() => setProjectBoardModal(null)}
        />

        <UserManagementDialogs
          modal={userManagementModal}
          onClose={() => setUserManagementModal(null)}
          onSuccess={(message) => {
            setSnackbar({ open: true, message, severity: 'success' });
            onRefresh?.();
          }}
          onError={(message) => setSnackbar({ open: true, message, severity: 'error' })}
        />
        {employeeManagerModal && (
          <EmployeeManagerDialog
            key={employeeManagerModal.command.key}
            open
            title={employeeManagerModal.command.caption}
            nodeKey={metadata.contentKey}
            employeeIds={employeeManagerModal.employeeIds}
            managerFieldName={employeeManagerModal.managerFieldName}
            managerFieldKey={employeeManagerModal.managerFieldKey}
            currentManagerIds={employeeManagerModal.currentManagerIds}
            defaultFilters={employeeManagerModal.defaultFilters}
            onClose={() => setEmployeeManagerModal(null)}
            onSuccess={(message) => {
              setEmployeeManagerModal(null);
              clearSelection();
              onRefresh?.();
              setSnackbar({ open: true, message, severity: 'success' });
            }}
          />
        )}

        {companyBankAccountModal && (
          <CompanyBankAccountDialog
            key={
              companyBankAccountModal.command.key +
              '-' +
              companyBankAccountModal.invoiceIds.join(',')
            }
            open
            title={companyBankAccountModal.command.caption}
            nodeKey={metadata.contentKey}
            invoiceIds={companyBankAccountModal.invoiceIds}
            targetFieldName={companyBankAccountModal.targetFieldName}
            onClose={() => setCompanyBankAccountModal(null)}
            onSuccess={(message) => {
              setSnackbar({ open: true, message, severity: 'success' });
              clearSelection();
              onRefresh?.();
              setCompanyBankAccountModal(null);
            }}
          />
        )}
        {subscriptionProofBankImportCommand && (
          <SubscriptionProofBankImportDialog
            key={subscriptionProofBankImportCommand.key}
            command={subscriptionProofBankImportCommand}
            nodeKey={metadata.contentKey}
            onClose={() => setSubscriptionProofBankImportCommand(null)}
            onSuccess={(message) => {
              setSubscriptionProofBankImportCommand(null);
              onRefresh?.();
              setSnackbar({ open: true, message, severity: 'success' });
            }}
          />
        )}
        {createTaskForTeamModal && (
          <CreateTaskForTeamDialog
            key={`${createTaskForTeamModal.command.key}-${createTaskForTeamModal.projectId}`}
            open
            title={createTaskForTeamModal.command.caption}
            projectId={createTaskForTeamModal.projectId}
            nodeKey={metadata.contentKey}
            onClose={() => setCreateTaskForTeamModal(null)}
            onSuccess={(message) => {
              setCreateTaskForTeamModal(null);
              onRefresh?.();
              setSnackbar({ open: true, message, severity: 'success' });
            }}
          />
        )}
        {createTasksAndWorkLogsModal && (
          <CreateTasksAndWorkLogsDialog
            key={`${createTasksAndWorkLogsModal.command.key}-${createTasksAndWorkLogsModal.projectId}`}
            open
            title={createTasksAndWorkLogsModal.command.caption}
            projectId={createTasksAndWorkLogsModal.projectId}
            nodeKey={metadata.contentKey}
            onClose={() => setCreateTasksAndWorkLogsModal(null)}
            onSuccess={(message) => {
              setCreateTasksAndWorkLogsModal(null);
              onRefresh?.();
              setSnackbar({ open: true, message, severity: 'success' });
            }}
          />
        )}

        <MissingEmployeeRatesDialog
          open={!!missingEmployeeRatesCommand}
          onClose={() => setMissingEmployeeRatesCommand(null)}
          onRateCreated={(message) => {
            onRefresh?.();
            setSnackbar({ open: true, message, severity: 'success' });
          }}
        />

        {bankStatementManagementImportCommand && (
          <BankStatementManagementImportDialog
            open
            title={bankStatementManagementImportCommand.caption}
            nodeKey={metadata.contentKey}
            onClose={() => setBankStatementManagementImportCommand(null)}
            onImported={(message) => {
              setBankStatementManagementImportCommand(null);
              onRefresh?.();
              setSnackbar({
                open: true,
                message: message || 'Bank statement imported successfully.',
                severity: 'success',
              });
            }}
          />
        )}

        <Snackbar
          open={snackbar.open}
          autoHideDuration={snackbar.severity === 'success' ? 6000 : 8000}
          onClose={handleSnackbarClose}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        >
          <Alert
            onClose={handleSnackbarClose}
            severity={snackbar.severity}
            variant="filled"
            sx={
              snackbar.severity === 'success'
                ? { ...sxSuccessAlert, maxWidth: 480 }
                : { width: '100%', maxWidth: 480 }
            }
          >
            <Box
              component="span"
              sx={{ display: 'block' }}
              dangerouslySetInnerHTML={{
                __html: DOMPurify.sanitize(snackbar.message, {
                  ALLOWED_TAGS: ['br', 'b', 'strong'],
                  ALLOWED_ATTR: [],
                }),
              }}
            />
          </Alert>
        </Snackbar>
        <JsMethodSupportNotifier fields={columns} host="grid" />
      </Box>
    );
  }
);
